import { CardModifier, ModifierEffect, RunAbility, NodeType, RewardRarity, UnlockState } from '../types';
import { getRng } from '../utils/Random';

interface ModifierTemplate {
  id: string;
  name: string;
  trigger: 'attack' | 'defense' | 'ability';
  effect: ModifierEffect;
  rarity: RewardRarity;
  description: string;
  abilityPack: number;
}

interface ModifierContext {
  battleNumber: number;
  nodeType: NodeType;
  unlockSnapshot: UnlockState;
  minRarity?: RewardRarity;
}

interface AbilityContext extends ModifierContext {
  ownedAbilityIds: string[];
}

interface VictoryContext {
  battleNumber: number;
  nodeType: NodeType;
  existingDeck: number[];
  ownedAbilityIds: string[];
  unlockSnapshot: UnlockState;
}

const RARITY_ORDER: RewardRarity[] = ['common', 'uncommon', 'rare'];

const MODIFIER_POOL: ModifierTemplate[] = [
  // Attack modifiers
  { id: 'sharp', name: 'Sharp', trigger: 'attack', effect: { type: 'extra_damage', value: 3 }, rarity: 'common', description: '+3 bonus damage', abilityPack: 1 },
  { id: 'piercing', name: 'Piercing', trigger: 'attack', effect: { type: 'extra_damage', value: 5 }, rarity: 'uncommon', description: '+5 bonus damage', abilityPack: 1 },
  { id: 'brutal', name: 'Brutal', trigger: 'attack', effect: { type: 'extra_damage', value: 8 }, rarity: 'rare', description: '+8 bonus damage', abilityPack: 1 },
  { id: 'vampiric', name: 'Vampiric', trigger: 'attack', effect: { type: 'heal', value: 2 }, rarity: 'common', description: 'Heal 2 on attack', abilityPack: 1 },
  { id: 'siphoning', name: 'Siphoning', trigger: 'attack', effect: { type: 'heal', value: 4 }, rarity: 'uncommon', description: 'Heal 4 on attack', abilityPack: 1 },
  { id: 'crushing', name: 'Crushing', trigger: 'attack', effect: { type: 'debuff_enemy', value: 3 }, rarity: 'uncommon', description: 'Enemy next attack -3', abilityPack: 1 },
  { id: 'devastating', name: 'Devastating', trigger: 'attack', effect: { type: 'debuff_enemy', value: 6 }, rarity: 'rare', description: 'Enemy next attack -6', abilityPack: 1 },
  { id: 'feint_step', name: 'Feint Step', trigger: 'attack', effect: { type: 'draw', value: 1 }, rarity: 'common', description: 'Draw +1 next turn', abilityPack: 1 },
  { id: 'opening_break', name: 'Opening Break', trigger: 'attack', effect: { type: 'zone_cap_attack', value: 1 }, rarity: 'uncommon', description: 'ATK cap +1 next turn', abilityPack: 1 },
  { id: 'battle_drum', name: 'Battle Drum', trigger: 'attack', effect: { type: 'zone_cap_all', value: 1 }, rarity: 'rare', description: 'All zone caps +1 next turn', abilityPack: 2 },
  { id: 'grim_focus', name: 'Grim Focus', trigger: 'attack', effect: { type: 'draw_penalty', value: 1 }, rarity: 'uncommon', description: 'Draw -1 next turn', abilityPack: 2 },

  // Defense modifiers
  { id: 'sturdy', name: 'Sturdy', trigger: 'defense', effect: { type: 'extra_block', value: 3 }, rarity: 'common', description: '+3 bonus block', abilityPack: 1 },
  { id: 'fortified', name: 'Fortified', trigger: 'defense', effect: { type: 'extra_block', value: 5 }, rarity: 'uncommon', description: '+5 bonus block', abilityPack: 1 },
  { id: 'ironclad', name: 'Ironclad', trigger: 'defense', effect: { type: 'extra_block', value: 8 }, rarity: 'rare', description: '+8 bonus block', abilityPack: 1 },
  { id: 'thorny', name: 'Thorny', trigger: 'defense', effect: { type: 'extra_damage', value: 2 }, rarity: 'common', description: 'Deal 2 thorns damage', abilityPack: 1 },
  { id: 'barbed', name: 'Barbed', trigger: 'defense', effect: { type: 'extra_damage', value: 4 }, rarity: 'uncommon', description: 'Deal 4 thorns damage', abilityPack: 1 },
  { id: 'regenerating', name: 'Regenerating', trigger: 'defense', effect: { type: 'heal', value: 3 }, rarity: 'uncommon', description: 'Heal 3 when defending', abilityPack: 1 },
  { id: 'restorative', name: 'Restorative', trigger: 'defense', effect: { type: 'heal', value: 6 }, rarity: 'rare', description: 'Heal 6 when defending', abilityPack: 1 },
  { id: 'tactical_wall', name: 'Tactical Wall', trigger: 'defense', effect: { type: 'draw', value: 1 }, rarity: 'common', description: 'Draw +1 next turn', abilityPack: 1 },
  { id: 'counterweight', name: 'Counterweight', trigger: 'defense', effect: { type: 'debuff_enemy', value: 2 }, rarity: 'common', description: 'Enemy next attack -2', abilityPack: 1 },
  { id: 'shield_drill', name: 'Shield Drill', trigger: 'defense', effect: { type: 'zone_cap_defense', value: 1 }, rarity: 'uncommon', description: 'DEF cap +1 next turn', abilityPack: 2 },
  { id: 'bulwark_orders', name: 'Bulwark Orders', trigger: 'defense', effect: { type: 'zone_cap_all', value: 1 }, rarity: 'rare', description: 'All zone caps +1 next turn', abilityPack: 2 },

  // Ability modifiers
  { id: 'empowering', name: 'Empowering', trigger: 'ability', effect: { type: 'buff_attack', value: 3 }, rarity: 'common', description: 'Attack +3 this turn', abilityPack: 1 },
  { id: 'overwhelming', name: 'Overwhelming', trigger: 'ability', effect: { type: 'buff_attack', value: 6 }, rarity: 'rare', description: 'Attack +6 this turn', abilityPack: 1 },
  { id: 'shielding', name: 'Shielding', trigger: 'ability', effect: { type: 'extra_block', value: 4 }, rarity: 'common', description: 'Gain 4 block', abilityPack: 1 },
  { id: 'warding', name: 'Warding', trigger: 'ability', effect: { type: 'extra_block', value: 7 }, rarity: 'uncommon', description: 'Gain 7 block', abilityPack: 1 },
  { id: 'draining', name: 'Draining', trigger: 'ability', effect: { type: 'heal', value: 4 }, rarity: 'uncommon', description: 'Heal 4 HP', abilityPack: 1 },
  { id: 'shattering', name: 'Shattering', trigger: 'ability', effect: { type: 'debuff_enemy', value: 5 }, rarity: 'uncommon', description: 'Enemy next attack -5', abilityPack: 1 },
  { id: 'strategy', name: 'Strategy', trigger: 'ability', effect: { type: 'draw', value: 1 }, rarity: 'rare', description: 'Draw +1 next turn', abilityPack: 1 },
  { id: 'channeling', name: 'Channeling', trigger: 'ability', effect: { type: 'extra_damage', value: 5 }, rarity: 'uncommon', description: 'Deal 5 direct damage', abilityPack: 1 },
  { id: 'obliterating', name: 'Obliterating', trigger: 'ability', effect: { type: 'extra_damage', value: 10 }, rarity: 'rare', description: 'Deal 10 direct damage', abilityPack: 1 },
  { id: 'vanguard_orders', name: 'Vanguard Orders', trigger: 'ability', effect: { type: 'zone_cap_attack', value: 1 }, rarity: 'common', description: 'ATK zone cap +1 next turn', abilityPack: 1 },
  { id: 'phalanx_orders', name: 'Phalanx Orders', trigger: 'ability', effect: { type: 'zone_cap_defense', value: 1 }, rarity: 'common', description: 'DEF zone cap +1 next turn', abilityPack: 1 },
  { id: 'focus_orders', name: 'Focus Orders', trigger: 'ability', effect: { type: 'zone_cap_ability', value: 1 }, rarity: 'common', description: 'ABL zone cap +1 next turn', abilityPack: 1 },
  { id: 'pulse_draw', name: 'Pulse Draw', trigger: 'ability', effect: { type: 'draw', value: 2 }, rarity: 'uncommon', description: 'Draw +2 next turn', abilityPack: 1 },
  { id: 'vital_bloom', name: 'Vital Bloom', trigger: 'ability', effect: { type: 'heal', value: 6 }, rarity: 'rare', description: 'Heal 6 HP', abilityPack: 1 },
  { id: 'gravity_field', name: 'Gravity Field', trigger: 'ability', effect: { type: 'draw_penalty', value: 1 }, rarity: 'common', description: 'Draw -1 next turn', abilityPack: 2 },
  { id: 'war_signal', name: 'War Signal', trigger: 'ability', effect: { type: 'debuff_enemy', value: 7 }, rarity: 'rare', description: 'Enemy next attack -7', abilityPack: 2 },

  // v0.2 tactical depth
  { id: 'insight_plus', name: 'Insight+', trigger: 'ability', effect: { type: 'draw', value: 2 }, rarity: 'uncommon', description: 'Draw +2 next turn', abilityPack: 2 },
  { id: 'taxing_rune', name: 'Taxing Rune', trigger: 'ability', effect: { type: 'draw_penalty', value: 1 }, rarity: 'uncommon', description: 'Draw -1 next turn', abilityPack: 2 },
  { id: 'frontline_orders', name: 'Frontline Orders', trigger: 'ability', effect: { type: 'zone_cap_attack', value: 1 }, rarity: 'uncommon', description: 'ATK zone cap +1 next turn', abilityPack: 2 },
  { id: 'guard_formation', name: 'Guard Formation', trigger: 'ability', effect: { type: 'zone_cap_defense', value: 1 }, rarity: 'uncommon', description: 'DEF zone cap +1 next turn', abilityPack: 2 },
  { id: 'focus_matrix', name: 'Focus Matrix', trigger: 'ability', effect: { type: 'zone_cap_ability', value: 1 }, rarity: 'uncommon', description: 'ABL zone cap +1 next turn', abilityPack: 2 },
  { id: 'battlefield_expansion', name: 'Battlefield Expansion', trigger: 'ability', effect: { type: 'zone_cap_all', value: 1 }, rarity: 'rare', description: 'All zone caps +1 next turn', abilityPack: 2 },
];

function rarityRank(r: RewardRarity): number {
  return RARITY_ORDER.indexOf(r);
}

function getBattleRarityCap(battleNumber: number): RewardRarity {
  if (battleNumber <= 2) return 'common';
  if (battleNumber <= 4) return 'uncommon';
  return 'rare';
}

function minRarityOf(a: RewardRarity, b: RewardRarity): RewardRarity {
  return rarityRank(a) < rarityRank(b) ? a : b;
}

function maxRarityOf(a: RewardRarity, b: RewardRarity): RewardRarity {
  return rarityRank(a) > rarityRank(b) ? a : b;
}

function getEffectiveRarityRange(
  battleNumber: number,
  unlockSnapshot: UnlockState,
  minRarity?: RewardRarity,
): { min: RewardRarity; max: RewardRarity } {
  const unlockCap = unlockSnapshot.maxRarity;
  const battleCap = getBattleRarityCap(battleNumber);
  const max = minRarityOf(unlockCap, battleCap);
  const min = minRarity ? maxRarityOf(minRarity, 'common') : 'common';
  if (rarityRank(min) > rarityRank(max)) {
    return { min: max, max };
  }
  return { min, max };
}

function buildModifierFromTemplate(template: ModifierTemplate): CardModifier {
  return {
    id: template.id,
    name: template.name,
    description: template.description,
    rarity: template.rarity,
    trigger: template.trigger,
    effect: { ...template.effect },
  };
}

function toRunAbility(template: ModifierTemplate): RunAbility {
  return {
    id: `${template.id}_${template.trigger}_${template.effect.type}_${template.effect.value}`,
    name: template.name,
    description: `Each turn, a random drawn card gains [${template.trigger.toUpperCase()}] ${template.description}`,
    modifier: buildModifierFromTemplate(template),
  };
}

function getAllNumbers(): number[] {
  const numbers: number[] = [];
  for (let i = -10; i <= 10; i++) {
    if (i !== 0) numbers.push(i);
  }
  return numbers;
}

function getTemplatePool(context: ModifierContext): ModifierTemplate[] {
  const { min, max } = getEffectiveRarityRange(context.battleNumber, context.unlockSnapshot, context.minRarity);

  return MODIFIER_POOL.filter(template =>
    template.abilityPack <= context.unlockSnapshot.abilityPack &&
    rarityRank(template.rarity) >= rarityRank(min) &&
    rarityRank(template.rarity) <= rarityRank(max),
  );
}

export interface NumberRewardOption {
  kind: 'number';
  number: number;
}

export interface AbilityRewardOption {
  kind: 'ability';
  ability: RunAbility;
}

export interface AugmentRewardOption {
  kind: 'augment';
  modifier: CardModifier;
  title: string;
  description: string;
}

export type VictoryReward = NumberRewardOption | AbilityRewardOption | AugmentRewardOption;

export function generateModifier(context: ModifierContext): { modifier: CardModifier; name: string; description: string } {
  const rng = getRng();
  let pool = getTemplatePool(context);

  if (pool.length === 0) {
    const fallbackMax = context.unlockSnapshot.maxRarity;
    pool = MODIFIER_POOL.filter(m => rarityRank(m.rarity) <= rarityRank(fallbackMax));
  }

  const template = rng.pick(pool);
  return {
    modifier: buildModifierFromTemplate(template),
    name: template.name,
    description: template.description,
  };
}

export function generateAbilityReward(context: AbilityContext): RunAbility | null {
  const rng = getRng();
  let pool = getTemplatePool(context)
    .map(toRunAbility)
    .filter(ability => !context.ownedAbilityIds.includes(ability.id));

  if (pool.length === 0) {
    pool = MODIFIER_POOL
      .filter(template => template.abilityPack <= context.unlockSnapshot.abilityPack)
      .map(toRunAbility)
      .filter(ability => !context.ownedAbilityIds.includes(ability.id));
  }

  if (pool.length === 0) return null;
  return rng.pick(pool);
}

function pickNumberRewards(existingDeck: number[], count: number, minAbsOne?: number): number[] {
  const rng = getRng();
  const allNumbers = getAllNumbers();
  // deckNumbers is a distinct set, so adding a number the deck already holds
  // does nothing. Prefer genuinely new numbers so a chosen card is always a
  // real addition rather than a dead pick.
  const unseenNumbers = allNumbers.filter(n => !existingDeck.includes(n));
  const pool = unseenNumbers.length > 0 ? unseenNumbers : allNumbers;

  const picks: number[] = [];

  if (minAbsOne && pool.some(n => Math.abs(n) >= minAbsOne)) {
    const premiumPool = pool.filter(n => Math.abs(n) >= minAbsOne);
    picks.push(rng.pick(premiumPool));
  }

  const shuffled = rng.shuffle([...pool]);
  for (const num of shuffled) {
    if (picks.length >= count) break;
    if (picks.includes(num)) continue;
    picks.push(num);
  }

  while (picks.length < count) {
    picks.push(rng.pick(allNumbers));
  }

  return picks;
}

export function generateVictoryRewards(context: VictoryContext): VictoryReward[] {
  const rng = getRng();
  const rewards: VictoryReward[] = [];

  const minElitePremium = context.nodeType === 'elite' ? 'uncommon' : undefined;
  const minBossPremium = context.nodeType === 'boss' ? 'rare' : undefined;

  // One meaningful number card (a real new number) sits alongside the always
  // useful ability and augment, instead of a screen of negative dead picks.
  const numberChoices = pickNumberRewards(
    context.existingDeck,
    1,
    context.nodeType === 'elite' ? 7 : undefined,
  );
  for (const number of numberChoices) {
    rewards.push({ kind: 'number', number });
  }

  const ability = generateAbilityReward({
    battleNumber: context.battleNumber,
    nodeType: context.nodeType,
    unlockSnapshot: context.unlockSnapshot,
    ownedAbilityIds: context.ownedAbilityIds,
    minRarity: minBossPremium ?? minElitePremium,
  });
  if (ability) {
    rewards.push({ kind: 'ability', ability });
  }

  const augment = generateModifier({
    battleNumber: context.battleNumber,
    nodeType: context.nodeType,
    unlockSnapshot: context.unlockSnapshot,
    minRarity: minBossPremium ?? minElitePremium,
  });
  rewards.push({
    kind: 'augment',
    modifier: augment.modifier,
    title: augment.name,
    description: augment.description,
  });

  if (context.nodeType === 'boss') {
    const hasRarePremium = rewards.some(reward => {
      if (reward.kind === 'ability') return reward.ability.modifier.rarity === 'rare';
      if (reward.kind === 'augment') return reward.modifier.rarity === 'rare';
      return false;
    });

    if (!hasRarePremium) {
      const rareAugment = generateModifier({
        battleNumber: context.battleNumber,
        nodeType: context.nodeType,
        unlockSnapshot: { ...context.unlockSnapshot, maxRarity: 'rare' },
        minRarity: 'rare',
      });
      rewards.push({
        kind: 'augment',
        modifier: rareAugment.modifier,
        title: rareAugment.name,
        description: rareAugment.description,
      });
    }
  }

  return rng.shuffle(rewards);
}

export function getModifierColor(trigger: string): string {
  switch (trigger) {
    case 'attack': return '#ff6666';
    case 'defense': return '#6688ff';
    case 'ability': return '#66cc66';
    default: return '#aaaaaa';
  }
}
